import React, { useCallback, useEffect, useState } from 'react';
import { CompassOutlined, EllipsisOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Layout, Menu, Modal, PageHeader, Table, Typography } from 'antd';
import { apiClient, apiErrorMessage } from '../../utils';
import { Area, Risk, RiskPriority, User } from '../../models';
import moment from 'moment';
import { RiskDetailPage } from '../risks';
import { RiskDocument } from '../../models/risk-document';

export function ActionsPage() {
  const { Content } = Layout;
  const { Text } = Typography;

  const [loading, setLoading] = useState(false);
  const [risks, setRisks] = useState<Risk[]>();
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailModalRisk, setDetailModalRisk] = useState<Risk>();

  const loadRisks = useCallback(() => {
    setLoading(true);
    apiClient
      .get(`risks?scope=area`)
      .then(response => {
        setRisks(response.data);
        setLoading(false);
      })
      .catch((reason: any) => {
        apiErrorMessage(reason);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadRisks();
  }, [loadRisks]);

  const onRefreshClick = () => {
    loadRisks();
  };

  const onRiskDetailSaved = () => {
    loadRisks();
  };

  const openDetailModalByRisk = (risk: Risk) => {
    setDetailModalRisk(risk);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailModalRisk(undefined);
  };

  const onTableActionClick = (e: any, id: string) => {
    if (e.key === 'details') {
      const risk = risks?.find(r => r.id === id);
      if (risk) {
        openDetailModalByRisk(risk);
      }
    }
  };

  const tableActions = (id: string) => {
    return (
      <Menu onClick={(e: any) => onTableActionClick(e, id)}>
        <Menu.Item key="details">
          <Text>Detaylar</Text>
        </Menu.Item>
      </Menu>
    );
  };

  const tableColumns: any[] = [
    {
      title: 'Risk no',
      dataIndex: 'riskNo',
      key: 'riskNo',
      render: (riskNo: string, risk: Risk) => (
        <Button type="link" onClick={() => openDetailModalByRisk(risk)}>
          {riskNo}
        </Button>
      ),
    },
    {
      title: 'SMAT',
      dataIndex: ['smat', 'smatNo'],
      key: 'smat',
    },
    {
      title: 'Alan',
      dataIndex: 'area',
      key: 'area',
      render: (area: Area) => area.code,
    },
    {
      title: 'Görüşülen kişi sayısı',
      dataIndex: 'interviewCount',
      key: 'interviewCount',
    },
    {
      title: 'Güvensiz davranış mı?',
      dataIndex: 'unsafeAction',
      key: 'unsafeAction',
      render: (unsafeAction: boolean) => (unsafeAction ? 'Evet' : 'Hayır'),
    },
    {
      title: 'Güvensiz davranış isim',
      dataIndex: 'unsafeActionUser',
      key: 'unsafeActionUser',
      render: (unsafeActionUser: User) => unsafeActionUser?.fullNameId,
    },
    {
      title: 'Olasılık',
      dataIndex: 'beforeProbability',
      key: 'beforeProbability',
    },
    {
      title: 'Şiddet',
      dataIndex: 'beforeSeverity',
      key: 'beforeSeverity',
    },
    {
      title: 'Risk puanı',
      dataIndex: 'beforeRiskGrade',
      key: 'beforeRiskGrade',
    },
    {
      title: 'Öncelik',
      dataIndex: 'priority',
      key: 'priority',
      render: (priority: RiskPriority) =>
        priority ? <span style={{ color: '#' + priority.color }}>{priority.name}</span> : <span>-</span>,
    },
    {
      title: 'Risk ve fırsatların tanımı',
      dataIndex: 'riskDescription',
      key: 'riskDescription',
    },
    {
      title: 'Karşı tedbir açıklaması / Fırsat',
      dataIndex: 'precautionDescription',
      key: 'precautionDescription',
    },
    {
      title: 'Dokümanlar',
      dataIndex: 'documents',
      key: 'documents',
      render: (documents: RiskDocument[]) => documents?.length || '-',
    },
    {
      title: 'Oluşturma',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: string) => moment(createdAt).format('DD.MM.yyyy HH:mm'),
    },
    {
      title: 'Alan sorumlusu',
      dataIndex: ['area', 'responsibleUser'],
      key: 'responsibleUser',
      render: (responsibleUser: User) => (responsibleUser ? responsibleUser.fullNameId : '-'),
    },
    {
      title: 'Oluşturan',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (createdBy: User) => createdBy.fullNameId,
    },
    {
      title: '',
      key: 'action',
      render: (_: any, risk: Risk) => (
        <div style={{ textAlign: 'right' }}>
          <Dropdown overlay={tableActions(risk.id)} trigger={['click']}>
            <Button size="small" icon={<EllipsisOutlined />} />
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={
          <>
            <CompassOutlined /> Aksiyonlar
          </>
        }
        extra={[
          <Button key="refresh" onClick={onRefreshClick} icon={<ReloadOutlined />}>
            Yenile
          </Button>,
        ]}
      />
      <Content>
        <Table columns={tableColumns} dataSource={risks} loading={loading} pagination={false} />
      </Content>
      <Modal title="Risk bildirimi" visible={detailModalOpen} width={'50%'} footer={null} onCancel={closeDetailModal} destroyOnClose={true}>
        <RiskDetailPage riskId={detailModalRisk?.id} onSaved={onRiskDetailSaved} />
      </Modal>
    </>
  );
}
